import { find } from "lodash";

import type { ContributionFormula, Segment, FamilyQuote } from "Utils/types";

export type FamilyInfo = {
  name: string;
  age: number;
  type: "employee" | "spouse" | "child";
  member_id: string;
};

type PricingType = "segment" | "age" | "composite_segment";

type CostRow = {
  rowId: string;
  type: "employee" | "spouse" | "child" | Segment;
  label: string;
  applyPrice: boolean;
  prices: Array<number>;
};

type DiscountRow = {
  rowId: string;
  type: "discount";
  label: string;
  applyPrice: boolean;
  prices: Array<number>;
};

type CoverageRow = CostRow | DiscountRow;

// Choose Coverage Contribution Rows
// Handles all the contribution and pricing logic for the employee choose
// coverage pages. Set up so React can iterate through the returned rows without
// any pricing logic.
//
// INPUT
// familyInfo: array of objects: Information about the employee enrolling
// and their family. Each object is a different member.
//   name: string: Family member's name. Used for display and finding a person
//     in this object.
//   age: int: Used for counting children that could be discounted.
//   type: string: employee/spouse/child
//   member_id: int: their server side id
// prices: object: Keyed off of planId, contains the prices for each plan.
//   pricing_type: string: age/segment
//   pricing: object/array: For age, we have an array of prices in the same
//     order as the array in familyInfo. For segment, we have an object
//     keyed by segment type with a value of the price for that segment.
// contributions: object: What type of contribution structure does the company
//   have and now much are they contribution towards the employee/dependents?
//   Also, for percentOfBasePlan contributions, include the id of the base plan.
//   type: string: percentage/fixed/percent_of_base_plan
//   employee: int:
//   dependent: int:
//   base_plan: string: ID of the base plan
// enrollment: array of strings: names of those who are enrolling in coverage or
//   the segment that is chosen.
//
// OUTPUT
// An array of row objects
// Each row contains a label, type, and prices for each plan.
//
// rowId: int/string: either the segment key or the person's member id
// label: string: what the row is called (either a person's name or segment)
// type: string: is this a cost or discount row? useful for styling
// prices: array of objects: each object is the raw price for a specific plan
//   planId: the id of the plan the price belongs to
//   price: the raw price
// applyPrice: boolean: do we include this person/segment/discount when
//   calculating the total cost to enroll
//
// example:
// age:
// [{
//   label: "bob",
//   type: "employee",
//   prices: {
//     171: 30,
//     172: 35,
//   },
//   applyPrice: true,
// },
// }]

const planTypeContributions = (planType: string, contributions: ContributionFormula): ContributionFormula => {
  if (planType in contributions) {
    return contributions[planType];
  }
  return contributions;
};

export const chooseCoverageRows = (
  familyInfo: Array<FamilyInfo>,
  prices: Array<FamilyQuote>,
  contributions: ContributionFormula,
  enrollment: Array<any>,
  planType: string,
): Array<CoverageRow> => {
  const pricingType = getPricingType(prices);

  // Flexible contributions have a planType key
  // to allow for different contributions by plan type.
  const contributionsForPlanType = planTypeContributions(planType, contributions);

  const pricingRows: Array<CoverageRow> = rowIds(pricingType, familyInfo).map(
    (rowId) =>
      ({
        rowId,
        label: rowLabel(pricingType, familyInfo, rowId),
        type: rowType(pricingType, familyInfo, rowId),
        prices: prices.map((q) => displayPriceFor(rowId, prices, familyInfo, contributionsForPlanType, q.planId)),

        applyPrice: isSelectedToEnroll(rowId, enrollment),
      }) as CostRow,
  );

  if (contributionsForPlanType.type === "fixed" && pricingType === "age") {
    // check if the company is making a contribution, some companies are cheap
    // but don't let their employees know that.
    if (contributionsForPlanType.employee > 0) {
      const fixedContributionEmployeeRow = fixedContributionRow(
        getEmployee(familyInfo),
        prices,
        contributionsForPlanType,
        familyInfo,
        enrollment,
        isEnrolling(getEmployee(familyInfo), enrollment),
      );

      // insert the employee row just after the employee price
      pricingRows.splice(1, 0, fixedContributionEmployeeRow);
    }

    if (contributionsForPlanType.dependent > 0 && getDependents(familyInfo).length > 0) {
      const fixedContributionDependentRow = fixedContributionRow(
        getDependents(familyInfo)[0],
        prices,
        contributionsForPlanType,
        familyInfo,
        enrollment,
        getDependents(familyInfo).filter((dependent) => isEnrolling(dependent, enrollment)).length > 0,
      );

      pricingRows.push(fixedContributionDependentRow);
    }
  }

  const children = childrenEligibleForDiscount(familyInfo);
  if (pricingType === "age" && children.length > 3) {
    const multipleChildDiscountRow = discountRow(
      familyInfo,
      prices,
      contributions,
      enrollment,
      children.filter((child) => isEnrolling(child, enrollment)).length > 4,
    );

    pricingRows.push(multipleChildDiscountRow);
  }
  return pricingRows;
};

export const totalCost = (rows: Array<CoverageRow>, planIndex: number) =>
  Math.max(
    rows
      .filter((row) => row.applyPrice && row.rowId !== "multiple_child_discount")
      .map((row) => row.prices[planIndex])
      .reduce((a, b) => a + b, 0),
    0,
  );

export const enrollmentForOverview = (prices: Array<FamilyQuote>, familyInfo: Array<FamilyInfo>): Array<string> => {
  if (getPricingType(prices) === "age") {
    return familyInfo.map((person) => person.member_id);
  }
  return ["ee_only"];
};

/// /////////////////////////////////////////////////////////////////////////////
//
// "PRIVATE"
/// /////////////////////////////////////////////////////////////////////////////

const rowIds = (pricingType, familyInfo): Array<string> => {
  switch (pricingType) {
    case "age":
      return familyInfo.map((person) => person.member_id);
    case "segment":
    case "composite_segment":
      return segmentRowIds(familyInfo);
  }

  throw new Error(`Invalid pricing type: ${JSON.stringify(pricingType)}`);
};

const rowLabel = (pricingType: PricingType, familyInfo, rowId): string => {
  switch (pricingType) {
    case "age": {
      const person: any = getPersonFromId(rowId, familyInfo);
      return person.name;
    }
    case "segment":
    case "composite_segment":
      return segmentRowLabel(rowId, familyInfo);
  }

  throw new Error(`Invalid pricing type: ${JSON.stringify(pricingType)}`);
};

// used for styling the different types of rows
const rowType = (pricingType: PricingType, familyInfo, rowId): "employee" | "spouse" | "child" | Segment => {
  switch (pricingType) {
    case "age":
      return (getPersonFromId(rowId, familyInfo) as any).type;
    case "segment":
    case "composite_segment":
      return rowId as any;
  }

  throw new Error(`Invalid pricing type: ${JSON.stringify(pricingType)}`);
};

const fixedContributionRow = (person: FamilyInfo, prices, contributions, familyInfo, enrollment, applyPrice: boolean): DiscountRow => ({
  rowId: `company_contribution_${person && person.type === "employee" ? "employee" : "dependent"}`,
  label: `Company Contribution - ${person && person.type === "employee" ? "Employee" : "Dependent"}`,
  type: "discount",
  prices: prices.map((q) => -fixedContribution(person, familyInfo, prices, contributions, enrollment, q.planId)),

  applyPrice,
});

const discountRow = (familyInfo, prices, contributions, enrollment, applyPrice): DiscountRow => ({
  rowId: "multiple_child_discount",
  label: "Multiple Child Discount",
  type: "discount",
  prices: prices.map((q) => -multipleChildDiscount(familyInfo, contributions, prices, enrollment, q.planId)),

  applyPrice,
});

const segmentRowIds = (familyInfo): Array<string> => {
  const rows = ["ee_only"];

  const spouse = familyInfo.find((person) => person.type === "spouse" || person.type === "domestic_partner");

  if (spouse) {
    rows.push("ee_spouse");
  }

  const children = familyInfo.filter((person) => person.type === "child");
  if (children.length > 0) {
    rows.push("ee_children");
  }

  if (spouse && children.length > 0) {
    rows.push("ee_family");
  }

  return rows;
};

const segmentRowLabel = (rowId, familyInfo): string => {
  const numChildren = familyInfo.filter((person) => person.type === "child").length;
  switch (rowId) {
    case "ee_only":
      return "Just Me";
    case "ee_spouse":
      return "Me and My Spouse";
    case "ee_children":
      return `Me and My ${numChildren === 1 ? "Child" : `${numChildren} Children`}`;
    case "ee_family":
      return `Me, My Spouse, and My ${numChildren === 1 ? "Child" : `${numChildren} Children`}`;
  }

  throw new Error(`Invalid row ID ${JSON.stringify(rowId)}`);
};

//
// CALCULATE PRICES
/// ////////////////////////////

export const displayPriceFor = (rowId: string, prices: Array<FamilyQuote>, familyInfo: any, contributions: any, planId: string): number => {
  const pricingType = getPricingType(prices);
  if (pricingType === "age") {
    const person: any = getPersonFromId(rowId, familyInfo);
    return displayPriceForPersonInOverview(person, prices, contributions, planId);
  }
  return displayPriceForSegmentInOverview(
    // $FlowFixMe - rowId must be Segment because pricingType is segment
    rowId,
    prices,
    contributions,
    planId,
  );
};

const displayPriceForSegmentInOverview = (rowId: Segment, prices, contributions, planId): number => {
  switch (contributions.type) {
    case "fixed":
      return priceForSegmentWithFixedContribution(rowId, prices, contributions, planId);

    case "percentage":
      return priceForSegmentWithPercentContribution(rowId, prices, contributions, planId);

    case "percent_of_base_plan":
      return priceForSegmentWithBasePlanContribution(rowId, prices, contributions, planId);
  }

  throw new Error(`Invalid contribution type ${JSON.stringify(contributions.type)}`);
};

const priceForSegmentWithFixedContribution = (rowId: Segment, prices, contributions, planId) => {
  if (rowId === "ee_only") {
    return getRawPrice(prices, planId, "ee_only") - contributions.employee;
  }
  return getRawPrice(prices, planId, rowId) - contributions.employee - contributions.dependent;
};

const priceForSegmentWithPercentContribution = (rowId: Segment, prices, contributions, planId) => {
  const rawPrice = getRawPrice(prices, planId, "ee_only");
  const employeePrice = rawPrice * (1 - contributions.employee / 100);
  if (rowId === "ee_only") {
    return employeePrice;
  }
  return employeePrice + (getRawPrice(prices, planId, rowId) - getRawPrice(prices, planId, "ee_only")) * (1 - contributions.dependent / 100);
};

const priceForSegmentWithBasePlanContribution = (rowId: Segment, prices, contributions, planId) =>
  getRawPrice(prices, planId, rowId) - percentOfBasePlanContributionForSegment(rowId, prices, contributions);

const percentOfBasePlanContributionForSegment = (rowId: Segment, prices, contributions) => {
  if (!contributions.base_plan) {
    throw new Error("No base plan set in contribution config");
  }
  const basePlanId = contributions.base_plan;
  const employeeContribution = getRawPrice(prices, basePlanId, "ee_only") * (contributions.employee / 100);
  if (rowId === "ee_only") {
    return employeeContribution;
  }
  return employeeContribution + (getRawPrice(prices, basePlanId, rowId) - getRawPrice(prices, basePlanId, "ee_only")) * (contributions.dependent / 100);
};

const displayPriceForPersonInOverview = (person, prices, contributions, planId): number => {
  const priceBeforeContribution = getRawPrice(prices, planId, person.member_id);
  switch (contributions.type) {
    case "fixed":
      return priceBeforeContribution;
    case "percentage":
      return priceBeforeContribution * (1 - contributions[personTypeToContributionType(person)] / 100);

    case "percent_of_base_plan":
      return Math.max(priceBeforeContribution - percentOfBasePlanContributionForPerson(person, prices, contributions), 0);
  }

  throw new Error(`Invalid contribution type ${JSON.stringify(contributions.type)}`);
};

const percentOfBasePlanContributionForPerson = (person, prices, contributions) => {
  if (!contributions.base_plan) {
    throw new Error("No base plan set in contribution config");
  }
  const priceOfBasePlanForPerson = getRawPrice(prices, contributions.base_plan, person.member_id);

  return priceOfBasePlanForPerson * (contributions[personTypeToContributionType(person)] / 100);
};

const fixedContribution = (person: FamilyInfo, familyInfo, prices, contributions, enrollment, planId): number => {
  if (personTypeToContributionType(person) === "employee") {
    const maxContribution = contributions.employee;
    const employeeCost = displayPriceFor(person.member_id, prices, familyInfo, contributions, planId);

    return employeeCost > maxContribution ? maxContribution : employeeCost;
  }
  const maxContribution = contributions.dependent;
  const dependentsCost = getDependents(familyInfo)
    .filter((dependent) => isEnrolling(dependent, enrollment))
    .map((dependent) => displayPriceFor(dependent.member_id, prices, familyInfo, contributions, planId))
    .reduce((a, b) => a + b, 0);
  return dependentsCost > maxContribution ? maxContribution : dependentsCost;
};

const multipleChildDiscount = (familyInfo, contributions, prices, enrollment, planId) => {
  const allChildren = childrenEligibleForDiscount(familyInfo);
  const enrollingChildren = allChildren.filter((child) => isEnrolling(child, enrollment));

  // make the assumption that the first child is representative of all children
  // to use for pricing.
  const firstOfChildren = allChildren[0];
  const numChildrenForFree = Math.max(enrollingChildren.length - 4, 0);
  return numChildrenForFree * displayPriceForPersonInOverview(firstOfChildren, prices, contributions, planId);
};

//
// "Selectors"
/// /////////////////////

// pricesKey can be either an index in the case of age pricing or an object key
// for segment pricing
const getRawPrice = (prices: Array<FamilyQuote>, planId: string, priceKey: string): number => {
  const planQuote = find(prices, { planId });
  if (!planQuote) {
    throw new Error(`Invalid plan ID: ${planId}`);
  }
  return planQuote.pricing[priceKey];
};

const getPricingType = (prices) => {
  const first: FamilyQuote = Object.values(prices)[0] as any;
  if (first) {
    return first.pricing_type;
  }
  return "age";
};

export const getPersonFromId = (rowId: (string | null | undefined) | number, familyInfo: Array<FamilyInfo>): FamilyInfo | null | undefined =>
  familyInfo.find((person) => person.member_id === rowId);

const childrenEligibleForDiscount = (familyInfo) => familyInfo.filter((person) => person.type === "child" && person.age <= 21);

const getDependents = (familyInfo) => familyInfo.filter((person) => person.type === "child" || person.type === "spouse" || person.type === "domestic_partner");

const getEmployee = (familyInfo: Array<FamilyInfo>): FamilyInfo => {
  const result = familyInfo.find((person) => person.type === "employee");
  if (!result) {
    throw new Error("Family has no employee");
  }
  return result;
};

const isEnrolling = (person: FamilyInfo | null | undefined, enrollment): boolean => (person ? enrollment.includes(person.member_id) : false);

export const employeeIsEnrolling = (familyInfo: Array<FamilyInfo>, enrollment: Array<string>) => {
  const employee = familyInfo.find((person) => person.type === "employee");
  return employee && enrollment.includes(employee.member_id);
};

const personTypeToContributionType = (person) => (person && person.type === "employee" ? "employee" : "dependent");

const isSelectedToEnroll = (rowId, enrollment) => enrollment.includes(rowId);
